/**
 * ProceduresChartTab — Procedures inner tab of the patient Chart shell
 * (PROD-736 PR C, read-only). Add / Edit / Delete dialog ships in a
 * follow-up.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/proc_tab.dart`.
 *
 * FHIR: `Procedure R4`. One row per resource.
 */

// A reported date shows as entered ("about 2023", "Unknown", "2019"), never as its placeholder day.
import { reportedDateLabel } from '@/encounter/lib/reported-date';
import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { useState } from 'react';
import { toast } from 'sonner';
import { Skeleton, ModalYesNo } from '@scripsteam/scrips-signal-ds';
import { usePatientProcedures } from '@/patients/services/use-patient-procedures';
import { useUpdateProcedure } from '@/patients/services/use-update-procedure';
import { ChartSectionHeader } from './chart-section-header';
import { ChartRowIconActions } from '@/shared/ui/components/chart-row-actions';
import { AddProcedureEditor } from './add-procedure-editor';
import { normalizePerformerDetails } from './add-allergy-dialog';
import {
  emptyProcedureCoding,
  normalizeProcedureCode,
} from '@/patients/lib/procedures';
import type {
  PatientProcedure,
  UpdateProcedureBody,
} from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';
import { CHART_TD_V2, CHART_TH_V2, chartCell, chartTableCard, type ChartCell } from './chart-table-v2';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

export interface ProceduresChartTabProps {
  patientId: string | null | undefined;
}

export function ProceduresChartTab({ patientId }: ProceduresChartTabProps) {
  const { data, isLoading, isError, refetch } = usePatientProcedures(patientId);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<PatientProcedure | null>(null);
  const [deleting, setDeleting] = useState<PatientProcedure | null>(null);
  const updateMut = useUpdateProcedure();
  const rows = (data ?? []).filter((p) => p.isDeleted !== true);
  useReportChartCount(isLoading ? null : rows.length);
  const cap = useRowCap(rows);

  const editorOpen = addOpen || editing !== null;

  const closeEditor = () => {
    setAddOpen(false);
    setEditing(null);
  };
  const startEdit = (p: PatientProcedure) => {
    setAddOpen(false);
    setEditing(p);
  };

  // Soft-delete via PUT /procedure/{id} with isDeleted: true.
  // Same strict asserter guard as Conditions / Allergies.
  const confirmDelete = async () => {
    if (!deleting?.identifier || !patientId) return;
    const p = deleting;
    const identifier: string = p.identifier!;
    if (!p.asserter) {
      toast.error(
        "Cannot delete — this record is missing its original asserter. Please contact support.",
      );
      return;
    }
    const body: UpdateProcedureBody = {
      onsetDateTimeDisplay: p.onsetDateTimeDisplay ?? null,
      identifier,
      partOf: null,
      clinicalStatus:
        p.clinicalStatus === 'Resolved' ? 'Resolved' : 'Active',
      category: p.category ?? null,
      code: normalizeProcedureCode(p.code),
      bodySite: emptyProcedureCoding(p.bodySite),
      dataAbsentReason: 'n/a',
      subject: p.subject ?? patientId,
      recorder: null,
      encounter: p.encounter ?? null,
      performer: null,
      asserter: p.asserter,
      reasonReference: null,
      reason: emptyProcedureCoding(p.reason),
      outcome: p.outcome?.trim() || 'n/a',
      compilation: p.compilation?.trim() || 'n/a',
      onsetDateTime: p.onsetDateTime ?? null,
      note: p.note ?? '',
      isDeleted: true,
      performerDetails: normalizePerformerDetails(p.performerDetails),
      createdDate: p.createdDate ?? null,
      speciality: null,
    };
    try {
      await updateMut.mutateAsync({
        patientId,
        procedureId: identifier,
        body,
      });
      toast.success(
        `Removed procedure: ${p.code?.displayName ?? 'Unknown'}`,
      );
      setDeleting(null);
    } catch {
      /* Global toast surfaces failure; keep dialog open. */
    }
  };

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to record procedures."
          />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
            icon="warning"
            title="Couldn't load procedures."
            ctaLabel="Retry"
            onCtaClick={() => void refetch()}
          />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-6 space-y-4">
        {addOpen ? (
          <AddProcedureEditor patientId={patientId} onClose={closeEditor} />
        ) : (
          <EmptyState
              icon="clipboard"
              title="No procedures recorded for this patient."
              ctaLabel="Add procedure"
              onCtaClick={() => setAddOpen(true)}
            />
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-3">
      <ChartSectionHeader
        title="Procedures"
        count={rows.length}
        onAdd={editorOpen ? undefined : () => setAddOpen(true)}
      />
      {editorOpen ? (
        <AddProcedureEditor
          key={editing?.identifier ?? 'add'}
          patientId={patientId}
          editing={editing}
          onClose={closeEditor}
        />
      ) : null}
      <div className={chartTableCard('rounded-lg border border-border-default overflow-hidden bg-surface-card')}>
        <table className="w-full text-sm">
          <thead className="bg-grey-100">
            <tr className="text-left text-text-muted">
              <Th>#</Th>
              <Th>Procedure</Th>
              <Th>Body site</Th>
              <Th>Outcome</Th>
              <Th>Performed</Th>
              <Th>Performer</Th>
              <Th>Reason</Th>
              <Th>Note</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {cap.visible.map((p, i) => (
              <tr
                key={p.identifier ?? `${i}-${p.code?.code ?? 'unknown'}`}
                className="border-t border-border-default hover:bg-grey-100/50"
              >
                <Td cell="index">{i + 1}</Td>
                <Td cell="name">
                  <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                    {p.code?.displayName ?? '—'}
                  </span>
                  {p.code?.code ? (
                    <div className="text-xs text-text-hint">
                      {p.code.code}
                    </div>
                  ) : null}
                </Td>
                <Td cell="text">{p.bodySite?.display ?? '—'}</Td>
                <Td cell="short">{outcomeBadge(p.outcome)}</Td>
                <Td date>{reportedDateLabel(p.onsetDateTime, p.onsetDateTimeDisplay, formatDate)}</Td>
                <Td cell="text">{p.performerDetails?.name ?? '—'}</Td>
                <Td cell="text">{p.reason?.display ?? '—'}</Td>
                <Td cell="note" tip={p.note?.trim() || '—'}>{p.note?.trim() || '—'}</Td>
                <Td align="right" cell="actions">
                  <ChartRowIconActions
                    editLabel={`Edit procedure: ${p.code?.displayName ?? 'Unknown'}`}
                    deleteLabel={`Delete procedure: ${p.code?.displayName ?? 'Unknown'}`}
                    onEdit={editorOpen ? undefined : () => startEdit(p)}
                    onDelete={
                      editorOpen ? undefined : () => setDeleting(p)
                    }
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ChartShowAll cap={cap} label="procedures" />

      {/* Delete confirm — Flutter copy parity. */}
      <ModalYesNo
      open={deleting !== null}
      onCancel={() => ((open) => {
          if (!open) setDeleting(null);
        })(false)}
      title="Are you sure?"
      message={<>Do you want to proceed on deleting{' '}
              <span className={v2Look('font-semibold text-brand-depth', V2_BODY_EMPHASIS)}>
                {deleting?.code?.displayName ?? 'this procedure'}
              </span>
              ?</>}
      cancelLabel="Cancel"
      confirmLabel={updateMut.isPending ? 'Deleting…' : 'Delete'}
      onConfirm={() => {
                void confirmDelete();
              }}
      loading={updateMut.isPending}
    />
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={chartCell(
        `px-3 py-2 text-xs font-bold uppercase tracking-wide ${
          align === 'right' ? 'text-right' : 'text-left'
        }`,
        CHART_TH_V2,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  date = false,
  cell,
  tip,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  /** What the column holds (the bridge sizes it: index, name, text, short, date, note, actions). */
  cell?: ChartCell;
  /** A note's full text for the app's instant tooltip, shown when the one-line cell is cut off. */
  tip?: string;
  /** A date: kept on one line (the bridge's "patient chart" block, `data-chart-cell="date"`). */
  date?: boolean;
}) {
  return (
    <td
      data-chart-cell={date ? 'date' : cell}
      data-tip={tip}
      className={chartCell(
        `px-3 py-2 align-top ${
          align === 'right' ? 'text-right' : 'text-left'
        }`,
        CHART_TD_V2,
      )}
    >
      {children}
    </td>
  );
}

function outcomeBadge(raw: string | null | undefined) {
  if (!raw) return <span className="text-text-hint">—</span>;
  const lower = raw.toLowerCase().trim();
  if (lower === 'n/a' || lower === '') return <span className="text-text-hint">—</span>;
  const palette =
    lower.includes('success') || lower.includes('improved')
      ? 'bg-green-100 text-green-700'
      : lower.includes('failed') || lower.includes('worsened')
        ? 'bg-red-100 text-red-700'
        : lower.includes('no change') || lower.includes('unchanged')
          ? 'bg-yellow-100 text-yellow-700'
          : 'bg-grey-100 text-text-secondary';
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${palette}`}
    >
      {raw}
    </span>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso || iso === 'n/a') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}
